import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PRODUCTS } from "@/data/site";
import { useLang } from "@/i18n";
import ProductArt from "@/components/art/ProductArt";
import ProductModal from "@/components/ProductModal";
import Icon from "@/components/Icon";
import { Reveal, Eyebrow } from "@/components/Reveal";

const SERVICE_MAP = {
    "Smarten Inverter": "Smarten Inverter",
    "Inverter Battery": "Inverter Battery",
    "Lithium Battery": "Lithium Battery",
    "Solar Panels": "Solar Panel",
    "Complete Solar Setup": "Complete Solar Setup",
    "CCTV Cameras": "CCTV Camera",
    "Broadband Connection": "Broadband Connection",
    "EV Chargers & E-Bikes": "EV Charger",
};

export default function Products() {
    const { t } = useLang();
    const [selected, setSelected] = useState(null);
    return (
        <section id="products" data-testid="products-section" className="relative py-24 md:py-32 lg:py-40 bg-navy">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Reveal>
                    <Eyebrow>{t.products.eyebrow}</Eyebrow>
                    <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-ink mt-4">
                        {t.products.heading}
                    </h2>
                    <p className="mt-4 max-w-xl text-ice/60 text-base md:text-lg">
                        {t.products.sub}
                    </p>
                </Reveal>

                <div className="mt-14 grid gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3">
                    {PRODUCTS.map((p, i) => {
                        const tr = t.products.items[p.id];
                        return (
                        <Reveal key={p.id} delay={(i % 3) * 0.12}>
                            <motion.article
                                data-testid={`product-card-${p.id}`}
                                whileHover={{ y: -8 }}
                                transition={{ type: "spring", stiffness: 300, damping: 22 }}
                                className="group relative h-full bg-surface border border-ink/5 rounded-2xl p-7 overflow-hidden hover:border-cyan2/30 hover:shadow-[0_20px_40px_rgba(0,0,0,0.4),0_0_0_1px_rgba(0,207,255,0.15)] transition-[border-color,box-shadow] duration-300"
                            >
                                <span className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-ice/40">
                                    {tr.category}
                                </span>
                                <div
                                    className="relative mt-4 h-44 rounded-xl overflow-hidden flex items-center justify-center"
                                    style={{
                                        background: `radial-gradient(circle at 50% 65%, ${p.accent}14, transparent 70%)`,
                                    }}
                                >
                                    <div className="relative w-full h-full transition-transform duration-500 group-hover:scale-105">
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="w-52 h-full">
                                                <ProductArt type={p.art} accent={p.accent} />
                                            </div>
                                        </div>
                                        <img
                                            src={`/images/products/${p.id}.png`}
                                            alt={`${tr.name} — Farakka Broadband, Murshidabad`}
                                            loading="lazy"
                                            onError={(e) => e.currentTarget.remove()}
                                            className="absolute inset-0 w-full h-full object-cover"
                                        />
                                    </div>
                                </div>
                                <h3 className="font-heading text-xl font-semibold text-ink mt-6">
                                    {tr.name}
                                </h3>
                                <p className="mt-2 text-sm text-ice/60 leading-relaxed">
                                    {tr.description}
                                </p>
                                <button
                                    data-testid={`product-cta-${p.id}`}
                                    onClick={() => setSelected(p)}
                                    className="mt-6 inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-semibold text-ink/85 group-hover:border-transparent group-hover:bg-gradient-to-r group-hover:from-electric group-hover:to-cyan2 group-hover:text-ink transition-all duration-300"
                                >
                                    {t.products.viewDetails}
                                    <Icon name="arrow" size={15} />
                                </button>
                            </motion.article>
                        </Reveal>
                        );
                    })}
                </div>
            </div>

            <AnimatePresence>
                {selected && (
                    <ProductModal
                        product={selected}
                        serviceName={SERVICE_MAP[selected.name] || "Other"}
                        onClose={() => setSelected(null)}
                    />
                )}
            </AnimatePresence>
        </section>
    );
}
